
import type { PropType } from 'vue'
import { defineComponent, onBeforeMount, reactive } from 'vue'

const HOURS_COUNT = 24

export default defineComponent({
    props: {
        startHour: {
            type: Number,
            default: 6
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        timeFrom: {
            type: [Date, null] as PropType<Date | null>,
            default: null
        },
        timeTo: {
            type: [Date, null] as PropType<Date | null>,
            default: null
        },
        isMain: {
            type: Boolean,
            default: false,
        },
        mainTimeRange: {
            type: Array as PropType<Array<Date | null>>,
            default: null
        },
        reservedTimeRange: {
            type: Array as PropType<Array<Array<Date>>>,
            default: () => []
        }
    },
    emits: ['time-from', 'time-to', 'change'],
    setup(props, { emit }) {
        const state = reactive({
            timeSlotsList: [] as Date[]
        })

        const initialize = () => {
            const currentDate = new Date(Date.now())

            state.timeSlotsList = Array.from({ length: HOURS_COUNT }, (_, i) => {
                const hour = (props.startHour + i) % HOURS_COUNT

                if (hour < props.startHour) {
                    return new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate() + 1, hour)
                }

                return new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate(), hour)
            })
        }

        onBeforeMount(initialize)

        const isTimeSlotDisabled = (timeSlot: Date): boolean => {
            if (props.disabled) return true
            if (props.isMain) return false

            if (props.mainTimeRange[0] && props.mainTimeRange[1]) {
                return isBefore(timeSlot, props.mainTimeRange[0]) || isAfter(timeSlot, props.mainTimeRange[1])
            }

            if (props.mainTimeRange[0] && !props.mainTimeRange[1]) {
                return isBefore(timeSlot, props.mainTimeRange[0])
            }

            return false
        }

        const isTimeSlotReserved = (timeSlot: Date): boolean => {
            if (props.reservedTimeRange.length > 0) {
                const range = props.reservedTimeRange.find((range) => (isSameDateTime(range[0], timeSlot) || isSameDateTime(range[1], timeSlot)) || (isBefore(timeSlot, range[1]) && isAfter(timeSlot, range[0])))

                if (Boolean(range)) {
                    return true
                }
            }

            return false
        }

        const isTimeSlotSelected = (timeSlot: Date): boolean => {
            if (props.timeFrom && isSameDateTime(props.timeFrom, timeSlot)) {
                return true
            }

            if (props.timeTo && isSameDateTime(props.timeTo, timeSlot)) {
                return true
            }

            if (props.timeFrom && props.timeTo) {
                return isAfter(timeSlot, props.timeFrom) && isBefore(timeSlot, props.timeTo)
            }

            return false
        }

        const isSameDateTime = (a: Date, b: Date): boolean => a.getTime() === b.getTime()
        const isBefore = (a: Date, b: Date): boolean => a.getTime() < b.getTime()
        const isAfter = (a: Date, b: Date): boolean => a.getTime() > b.getTime()

        const onClick = (timeSlot: Date) => {
            if (props.disabled) return

            if (isTimeSlotDisabled(timeSlot) || isTimeSlotReserved(timeSlot)) {
                return
            }

            if (Boolean(props.timeTo)) {
                emit('time-to', null)
                emit('time-from', timeSlot)
                emit('change', { timeFrom: timeSlot, timeTo: null })

                return
            }

            if (Boolean(props.timeFrom)) {
                if (isSameDateTime(props.timeFrom as Date, timeSlot)) {
                    return
                }

                if (props.reservedTimeRange.length > 0) {
                    if (props.reservedTimeRange.some((range) => {
                        let dateTo = timeSlot
                        let dateFrom = props.timeFrom as Date

                        if (isBefore(timeSlot, props.timeFrom as Date)) {
                            dateTo = props.timeFrom as Date
                            dateFrom = timeSlot
                        }

                        return isBefore(range[0], dateTo) && isAfter(range[1], dateFrom)
                    })) {
                        return
                    }
                }

                if (isBefore(timeSlot, props.timeFrom as Date)) {
                    emit('time-to', props.timeFrom)
                    emit('time-from', timeSlot)
                    emit('change', { timeFrom: timeSlot, timeTo: props.timeFrom })

                    return
                }

                emit('time-to', timeSlot)
                emit('change', { timeFrom: props.timeFrom, timeTo: timeSlot })

                return
            }

            emit('time-from', timeSlot)
            emit('change', { timeFrom: timeSlot, timeTo: null })
        }

        const getDateHour = (date: Date): string => String(date.getHours()).padStart(2, '0')

        const formatDateTime = (date: Date): string => {
            const hours = String(date.getHours()).padStart(2, '0')
            const minutes = String(date.getMinutes()).padStart(2, '0')
            return `${hours}:${minutes}`
        }

        return {
            state,
            isTimeSlotDisabled,
            isTimeSlotReserved,
            isTimeSlotSelected,
            onClick,
            getDateHour,
            formatDateTime
        }
    }
})
